import { useMemo } from 'react';
import type { VoidFunctionComponent } from 'react';

import { plural } from '../../../../../shared/helpers/plural';
import { ExceptionIgnoreScope, ExceptionPackageScope } from '../../../../../shared/issuesV2';
import {
  AutoIgnoreReasons,
  IgnoredKind,
  InternalResolutionReasonDescriptions,
} from '../../../../../shared/issuesV2/common';
import Locator from '../../../../../shared/Locator';
import Badge from '../../../generic/Badge/Badge';
import Tooltip from '../../../generic/Tooltip';
import type { AutoIgnoreReason, IgnoredKindMetadata } from '../../../../../shared/issuesV2/common';

import IssueItemBadge from './IssueItemBadge';

export type Props = {
  ignoredKinds: IgnoredKindMetadata[];
  numProjects: number;
};

/**
 * IgnoredKindBadge only ever renders a badge for exception- and auto-ignores; Manual ignores
 * are surfaced elsewhere. Callers must gate rendering with this before mounting the badge.
 */
export const hasVisibleIgnoredKindBadge = (ignoredKinds: IgnoredKindMetadata[] | undefined): boolean =>
  !!ignoredKinds?.some(
    (ignore) => ignore.ignoreType === IgnoredKind.IssueException || ignore.ignoreType === IgnoredKind.AutoIgnore,
  );

const ignoreScopeMessages = {
  [ExceptionIgnoreScope.Project]: 'Ignored by project',
  [ExceptionIgnoreScope.ProjectAndReleaseGroups]: 'Ignored Proj + RG',
  [ExceptionIgnoreScope.ReleaseGroup]: 'Ignored by release group',
  [ExceptionIgnoreScope.Policy]: 'Ignored by policy',
  [ExceptionIgnoreScope.Organization]: 'Ignored globally',
};

// Short badge copy for known auto-ignore reasons. Any reason without an entry here falls back
// to its InternalResolutionReasonDescriptions description, so new reasons never go unlabeled.
const autoIgnoreBadgeCopy: Partial<Record<AutoIgnoreReason, string>> = {
  [AutoIgnoreReasons.NotDistributed]: 'Ignored by not distributed',
  [AutoIgnoreReasons.NonConcluded]: 'Non-concluded license',
};

/**
 * Ignored kind issue badge.
 */
const IgnoredKindBadge: VoidFunctionComponent<Props> = ({ ignoredKinds, numProjects }) => {
  const badges = useMemo(() => ignoredKinds
    // Callers dedupe ignoredKind across a project's projects with `_.omit(p.ignoredKind, ...)`, which
    // turns a project with no ignoredKind (e.g. an active project alongside ignored ones) into `{}`
    // rather than dropping it. Skip those instead of rendering a label-less badge.
    .filter((metadata): metadata is IgnoredKindMetadata => !!metadata?.ignoreType)
    .map((metadata) => {
      if (metadata.ignoreType === IgnoredKind.Manual) {
        return (
          <Badge color="light-gray" key="manual">
            Manual
          </Badge>
        );
      }

      if (metadata.ignoreType === IgnoredKind.AutoIgnore) {
        const description = InternalResolutionReasonDescriptions[metadata.reason];
        return (
          <Badge color="light-gray" key={`auto-ignore-${metadata.reason}`}>
            <Tooltip id={`auto-ignore-tooltip-${metadata.reason}`} tooltip={description}>
              <span>
                {autoIgnoreBadgeCopy[metadata.reason] ?? description}
              </span>
            </Tooltip>
          </Badge>
        );
      }

      /*
       * When the projects list is empty, it's the global issues scope. IssueItem receives
       *   showProjectsList={scope.type === 'global'}
       * from IssuesTable, and then all of the CategoryIssueItem components have
       *   projects={showProjectsList ? issue.projects : []}
       * Therefore, we know it's global scope when the project list is empty.
       */
      const isGlobal = !!numProjects;

      let tooltipText = 'All versions';
      if (isGlobal) {
        tooltipText = `Ignored in ${numProjects} ${plural(numProjects, 'project')}`;
      } else if (metadata.packageScope === ExceptionPackageScope.SelectedVersion) {
        // We know the revisionLocator always exists if the packageScope is for a selected version.
        tooltipText = `vers. ${Locator.parse(metadata.revisionLocator as string).revision}`;
      }

      return (
        <Badge color="light-gray" key={`exception-${metadata.issueExceptionId}`}>
          <Tooltip id="ignored-kind-tooltip" tooltip={tooltipText}>
            <span>
              {ignoreScopeMessages[metadata.ignoreScope]}
            </span>
          </Tooltip>
        </Badge>
      );
    }), [ignoredKinds, numProjects]);

  return (
    <IssueItemBadge>
      {badges}
    </IssueItemBadge>
  );
};

export default IgnoredKindBadge;
